import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LegalFooter } from "@/components/LegalFooter";
import {
  Bot, Brain, CalendarCheck, Check, Crown, FileText, FlaskConical, Gauge, GraduationCap, HandHeart, Heart, HeartHandshake,
  HeartPulse, Mic, PawPrint, Route, Shield, ShieldCheck, Smartphone, Smile, Sparkles, Users, UtensilsCrossed, Zap,
} from "lucide-react";

const FEATURES = [
  { icon: Gauge, title: "Explainable Bond Score", text: "A 0-100 relationship wellness indicator with trend, confidence labels, and the exact factors behind it. Never a diagnosis." },
  { icon: CalendarCheck, title: "Dual Check-Ins", text: "Track both sides of the bond — your mood and your animal's — in 30 seconds a day." },
  { icon: HandHeart, title: "Gentle Rituals", text: "Low-pressure, consent-based bonding routines. Let the AI craft one tailored to your pet's triggers and comforts." },
  { icon: Brain, title: "Relationship Intelligence", text: "Five explainable dimensions, honest risk notices, and next best actions — with data limits stated plainly." },
  { icon: Sparkles, title: "AI Bond Coach", text: "A safety-guarded coach that summarizes trends and suggests safe next steps. It escalates to professionals when needed." },
  { icon: FileText, title: "Vet-Ready Reports", text: "Organized observation timelines your veterinarian can scan in minutes. Shared only with your consent." },
  { icon: Users, title: "Care Circle", text: "Coordinate caregivers, sitters, fosters, and providers with scoped permissions." },
  { icon: Mic, title: "Voice Support", text: "Hands-free support agent with human escalation for emergencies, billing, and privacy requests." },
  { icon: Bot, title: "AI Care Specialists", text: "Six focused specialists that read your pet's live data and give clear recommendations on health, mood, nutrition, training, bonding, and daily routines." },
];

const SAFETY = [
  "Never diagnoses medical conditions",
  "Never prescribes medication",
  "Never replaces your veterinarian",
  "Escalates emergencies to professionals",
  "States confidence and data limits honestly",
  "Shares nothing without your consent",
];

const PLAN_META = {
  basic: { name: "Basic", icon: Shield, tagline: "Everything one pet needs, every day", features: ["1 pet profile with full history", "Daily check-ins, streaks & wellness logs", "Fresh personalized ritual + enrichment plan daily", "Full Bond Score & AI Bond Coach", "Vet-ready reports"] },
  pro: { name: "Pro", icon: Zap, tagline: "For multi-pet homes & care teams", highlight: true, features: ["Everything in Basic — for up to 3 pets", "Care Circle: share with sitters, family & your vet", "Voice Support agent", "AI Specialists team (behavior, nutrition, training...)"] },
  premium: { name: "Premium", icon: Crown, tagline: "The full wellness OS, no limits", features: ["Everything in Pro — unlimited pets", "Unlimited vet-ready reports", "Full Agent Command Center", "Priority human support"] },
};

const SPECIALISTS = [
  { icon: HeartPulse, name: "Health Insights Analyst", text: "Reads your wellness logs, spots trends early, and preps clear vet-visit summaries." },
  { icon: Smile, name: "Mood & Behavior Coach", text: "Explains behavior patterns from daily check-ins and recommends calming strategies." },
  { icon: Sparkles, name: "Bond & Enrichment Planner", text: "Builds personalized weekly bonding plans from your Bond Score and ritual history." },
  { icon: Route, name: "Daily Care Optimizer", text: "Fine-tunes feeding, exercise, and rest routines for your pet's breed, age, and energy." },
  { icon: UtensilsCrossed, name: "Nutrition & Weight Guide", text: "Watches appetite and weight signals and gives practical, safe feeding guidance." },
  { icon: GraduationCap, name: "Training & Skills Coach", text: "Designs force-free training plans around your pet's actual traits and triggers." },
];

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  useEffect(() => {
    api.get("/billing/prices").then(({ data }) => setPricing(data)).catch(() => {});
  }, []);
  const logoTarget = user ? (user.role === "admin" ? "/admin" : "/dashboard") : "/";
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 bg-[hsl(var(--background)/0.92)] backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button className="flex items-center gap-2" onClick={() => navigate(logoTarget)} data-testid="landing-logo-button">
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center"><PawPrint className="w-4 h-4 text-primary-foreground" /></div>
            <span className="font-display text-lg font-semibold">Soula Paws</span>
          </button>
          <div className="flex items-center gap-2">
            {user ? (
              <Button className="rounded-xl" onClick={() => navigate(logoTarget)} data-testid="landing-open-app-button">Open my dashboard</Button>
            ) : (
              <>
                <Button variant="outline" size="sm" className="rounded-xl h-8 px-2.5 text-xs sm:h-10 sm:px-4 sm:text-sm border-[hsl(var(--warning)/0.5)] text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning)/0.08)] hover:text-[hsl(var(--warning))]" onClick={() => navigate("/register?beta=1")} data-testid="landing-join-beta-button">
                  <FlaskConical className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-1.5" /><span className="hidden sm:inline">Join the beta</span><span className="sm:hidden ml-1">Beta</span>
                </Button>
                <Link to="/download" className="hidden md:block"><Button variant="ghost" data-testid="landing-get-app-button"><Smartphone className="w-4 h-4 mr-1.5" /> Get the app</Button></Link>
                <Link to="/login"><Button variant="ghost" data-testid="landing-login-button">Log in</Button></Link>
                <Link to="/register"><Button className="rounded-xl" data-testid="landing-register-button">Start gently</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="noise-overlay bg-[radial-gradient(1200px_circle_at_20%_10%,hsl(18_42%_90%)_0%,transparent_55%),radial-gradient(900px_circle_at_80%_0%,hsl(174_38%_90%)_0%,transparent_50%)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <Badge variant="secondary" className="mb-5 border-0 bg-[hsl(var(--accent)/0.5)]">The relationship intelligence layer for you and your animal</Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08]">
              Care for the bond,<br />not just the pet.
            </h1>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed max-w-lg">
              Soula Paws is a privacy-first AI wellness OS for the emotional, physical, and behavioral bond between people and their animals — for owners, caregivers, fosters, rescues, and senior-pet families.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button size="lg" className="rounded-xl shadow-[0_10px_30px_-18px_rgba(0,0,0,0.35)]" data-testid="landing-primary-cta-button">Start gently — choose your plan</Button></Link>
              <a href="#safety"><Button size="lg" variant="secondary" className="rounded-xl" data-testid="landing-safety-cta-button">See safety commitments</Button></a>
            </div>
            <p className="mt-4 text-xs text-muted-foreground">You control what's shared. Every AI output carries a confidence label.</p>
          </div>
          <div className="relative">
            <img
              src="/hero-pet.jpg"
              alt="A person gently bonding with their dog at home"
              className="rounded-3xl border border-border shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)] w-full object-cover aspect-[4/3]"
            />
            <Card className="absolute -bottom-6 -left-4 md:-left-8 rounded-2xl shadow-lg">
              <CardContent className="p-4 flex items-center gap-3">
                <Gauge className="w-8 h-8 text-primary" />
                <div>
                  <p className="text-sm font-semibold">Bond Score 82 · Improving</p>
                  <p className="text-xs text-muted-foreground">Confidence: High · Why? 14 check-ins, 5 rituals</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-center">Not another pet tracker.</h2>
          <p className="text-center text-muted-foreground mt-2 max-w-xl mx-auto">Trackers count steps. Soula Paws understands the relationship — human emotional context, animal wellness, rituals, and care-circle coordination in one intelligence layer.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {FEATURES.map((f) => (
              <Card key={f.title} className="rounded-2xl hover:shadow-[0_18px_50px_-30px_rgba(0,0,0,0.35)] transition-shadow duration-200">
                <CardContent className="p-5">
                  <div className="w-9 h-9 rounded-xl bg-secondary flex items-center justify-center mb-3"><f.icon className="w-4.5 h-4.5 text-primary w-5 h-5" /></div>
                  <p className="text-sm font-semibold">{f.title}</p>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="specialists" className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-center">Meet your AI care team.</h2>
          <p className="text-center text-muted-foreground mt-2 max-w-2xl mx-auto">
            Six focused specialists that read your pet's live data — logs, check-ins, Bond Score, triggers and comforts — and give clear, personal recommendations. Observation-based, never a diagnosis.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10" data-testid="landing-specialists-grid">
            {SPECIALISTS.map((s) => (
              <Card key={s.name} className="rounded-2xl hover:shadow-[0_18px_50px_-30px_rgba(0,0,0,0.35)] transition-shadow duration-200" data-testid={`landing-specialist-${s.name.split(" ")[0].toLowerCase()}`}>
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center"><s.icon className="w-5 h-5 text-primary" /></div>
                  <p className="font-semibold mt-3">{s.name}</p>
                  <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-14 md:py-20 bg-secondary/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-center">Start free. Grow when you're ready.</h2>
          <p className="text-center text-muted-foreground mt-2 max-w-xl mx-auto">
            Try Soula Paws <strong>free for {pricing?.freeTier?.days ?? 30} days with one pet — no card required</strong>. Paid plans include a {pricing?.trialDays ?? 14}-day free trial. Cancel anytime.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10" data-testid="landing-pricing-grid">
            <Card className="rounded-2xl flex flex-col border-dashed" data-testid="landing-pricing-free-card">
              <CardContent className="p-6 flex flex-col flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-semibold flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-primary" /> Free</p>
                  <Badge variant="secondary" className="border-0 bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] text-[10px]">No card</Badge>
                </div>
                <p className="text-3xl font-bold mt-3">$0<span className="text-sm text-muted-foreground font-normal"> / {pricing?.freeTier?.days ?? 30} days</span></p>
                <p className="text-xs text-muted-foreground mt-0.5">Test the product with one pet</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {["1 pet profile", "Daily check-ins & Bond Score", "Personalized daily ritual + ideas", "Starter AI Coach"].map((f) => (
                    <li key={f} className="text-sm flex gap-2"><Check className="w-4 h-4 text-[hsl(var(--success))] shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                <Link to="/register" className="mt-5">
                  <Button className="w-full rounded-xl" variant="outline" data-testid="landing-pricing-cta-free">Start free — no card</Button>
                </Link>
              </CardContent>
            </Card>
            {(pricing?.tiers || []).map((tier) => {
              const meta = PLAN_META[tier.plan];
              if (!meta) return null;
              const Icon = meta.icon;
              return (
                <Card key={tier.plan} className={`rounded-2xl flex flex-col ${meta.highlight ? "border-[hsl(var(--warning)/0.5)] shadow-[0_18px_50px_-30px_rgba(0,0,0,0.35)]" : ""}`} data-testid={`landing-pricing-${tier.plan}-card`}>
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold flex items-center gap-1.5"><Icon className={`w-4 h-4 ${tier.plan === "premium" ? "text-[hsl(var(--warning))]" : "text-primary"}`} /> {meta.name}</p>
                      {meta.highlight && <Badge variant="secondary" className="border-0 bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))] text-[10px]">Popular</Badge>}
                    </div>
                    <p className="text-3xl font-bold mt-3">${tier.amount}<span className="text-sm text-muted-foreground font-normal"> / month</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">{meta.tagline}</p>
                    <ul className="mt-4 space-y-2 flex-1">
                      {meta.features.map((f) => <li key={f} className="text-sm flex gap-2"><Check className="w-4 h-4 text-[hsl(var(--success))] shrink-0 mt-0.5" />{f}</li>)}
                    </ul>
                    <Link to="/register" className="mt-5">
                      <Button className="w-full rounded-xl" variant={meta.highlight ? "default" : "outline"} data-testid={`landing-pricing-cta-${tier.plan}`}>
                        Start {pricing?.trialDays ?? 14}-day free trial
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <p className="text-center text-sm mt-8">
            <button onClick={() => navigate("/register?beta=1")} className="text-[hsl(var(--warning))] font-medium hover:underline inline-flex items-center gap-1.5" data-testid="landing-pricing-beta-link">
              <FlaskConical className="w-4 h-4" /> Or join the beta: {pricing?.betaTrialDays ?? 60} days of Premium free + 10% off for life →
            </button>
          </p>
        </div>
      </section>

      <section id="safety" className="py-14 md:py-20 bg-secondary/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 mb-4"><ShieldCheck className="w-6 h-6 text-primary" /><h2 className="font-display text-2xl md:text-3xl font-semibold">Our safety commitments</h2></div>
            <p className="text-muted-foreground leading-relaxed">Soula Paws is a wellness support platform — not a veterinarian, therapist, or emergency service. Every AI feature is bounded by non-negotiable guardrails and audit logs.</p>
            <div className="mt-6 space-y-2.5">
              {SAFETY.map((s) => (
                <div key={s} className="flex items-center gap-2.5">
                  <Shield className="w-4 h-4 text-[hsl(var(--success))]" />
                  <span className="text-sm">{s}</span>
                </div>
              ))}
            </div>
          </div>
          <Card className="rounded-2xl">
            <CardContent className="p-6">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Example AI insight</p>
              <p className="text-sm leading-relaxed">"Bond Score decreased from 78 to 64 this week. Main factors: ritual completion dropped from 5 days to 1, pet anxiety logs increased, owner stress increased. Suggested next step: try a 5-minute calm connection ritual tonight."</p>
              <p className="text-xs text-muted-foreground italic mt-4">This is not a diagnosis. If symptoms worsen or urgent signs appear, contact a veterinarian.</p>
              <div className="flex gap-2 mt-4">
                <Badge variant="secondary" className="border-0 bg-[hsl(var(--confidence-med)/0.12)] text-[hsl(var(--confidence-med))]">Confidence: Medium</Badge>
                <Badge variant="secondary" className="border-0 bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]">Needs attention</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-16 md:py-24 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <HeartHandshake className="w-10 h-10 text-primary mx-auto mb-4" />
          <h2 className="font-display text-3xl md:text-4xl font-semibold">One small step tonight.</h2>
          <p className="text-muted-foreground mt-3">Start with a 30-second check-in and a 5-minute calm connection ritual. You're not alone in this.</p>
          <Link to="/register"><Button size="lg" className="mt-7 rounded-xl" data-testid="landing-footer-cta-button">Create your account</Button></Link>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}
